import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook for client access control.
 * Provides utilities to check user's client access based on Keycloak groups.
 */
export const useClientAccess = () => {
  const { user, serverScope } = useAuth();

  return useMemo(() => {
    // Check if user is SuperAdmin (has access to all clients)
    const isSuperAdmin = user?.roles.includes('SuperAdmin') ?? false;
    const isAdmin = user?.roles.includes('Admin') ?? false;
    const isClientAdmin = user?.roles.includes('ClientAdmin') ?? false;
    const isOperator = user?.roles.includes('Operator') ?? false;
    const isViewer = user?.roles.includes('Viewer') ?? false;

    // Backward compat: Operator treated as ClientAdmin equivalent
    const isClientAdminOrOperator = isClientAdmin || isOperator;

    // ---------------------------------------------------------------------
    // Two planes of authority.
    //
    // ANEP staff run the platform: they onboard customer organisations and
    // own the shared catalogue every tenant draws from — device types,
    // hardware types, config templates, firmware. What they change affects
    // every client.
    //
    // Client staff run one organisation: their own devices and their own
    // technicians. They consume the catalogue but never edit it, which is
    // already how the API is gated (require_admin / require_superadmin on
    // every catalogue mutation). Hiding these screens aligns what is shown
    // with what is permitted, instead of offering actions that 403.
    // ---------------------------------------------------------------------
    // Platform staff is decided by group membership, not by role name:
    // `Viewer` exists in both spaces. SuperAdmin counts regardless, as it is
    // an ANEP-only role that predates the group.
    // A recognised application role on this environment's client. Anything
    // else the realm hands out (default-roles, offline_access) is not one.
    const hasApplicationRole =
      isSuperAdmin || isAdmin || isClientAdmin || isOperator || isViewer;

    const isAnepStaff = isSuperAdmin || (user?.isAnepStaff ?? false);
    const isClientStaff = !isAnepStaff && isClientAdminOrOperator;
    // Who may CHANGE the platform. Seeing it is a separate question: an ANEP
    // Viewer reaches every ANEP screen and reads them, but every control that
    // writes is gated on this instead.
    const isAnepManager = isSuperAdmin || isAdmin;

    // Get the list of client IDs the user has access to
    // The server's answer wins once it arrives: it accounts for a suspended
    // client, which the token cannot express. Until then, fall back to the
    // token so the UI is not empty during the first render.
    const allowedClientIds = serverScope
      ? serverScope.clientIds
      : (user?.clientIds ?? []);
    // Null means "not answered yet", never "no access".
    const scopeResolved = serverScope != null;

    // Check if user has only Viewer role (no Admin, SuperAdmin, ClientAdmin, or Operator)
    const hasOnlyViewerRole = isViewer && !isSuperAdmin && !isAdmin && !isClientAdminOrOperator;

    // Users section: visible to ClientAdmin, Admin, SuperAdmin
    const canManageUsers = isSuperAdmin || isAdmin || isClientAdminOrOperator;

    // Device types: ClientAdmin+ can edit settings, hardware types, model refs
    const canEditDeviceTypes = isSuperAdmin || isAdmin || isClientAdminOrOperator;

    // Check if user has only one client assigned
    const hasSingleClient = allowedClientIds.length === 1;

    return {
      /**
       * Whether the user is a SuperAdmin with unrestricted access
       */
      isSuperAdmin,

      /**
       * Whether the user is an Admin
       */
      isAdmin,

      /**
       * Whether the user is a ClientAdmin
       */
      isClientAdmin,

      /**
       * Whether the user is an Operator
       */
      isOperator,

      /**
       * Whether the user is a Viewer only
       */
      isViewerOnly: hasOnlyViewerRole,

      /**
       * ANEP-side staff: run the platform, own the shared catalogue, see
       * every customer organisation. Includes an ANEP Viewer, who reaches
       * every ANEP screen but changes nothing.
       */
      isAnepStaff,

      /**
       * Holds a recognised application role on this environment.
       */
      hasApplicationRole,

      /**
       * ANEP staff who may change the platform, as opposed to merely reading
       * it. This is what separates an ANEP Viewer from an Admin: same
       * screens, no write controls.
       */
      canManageClients: isAnepManager,

      /**
       * Uploading firmware and scheduling rollouts. Same distinction.
       */
      canManageFirmware: isAnepManager,

      /**
       * Customer-side staff: run one organisation's devices and users.
       */
      isClientStaff,

      /**
       * The shared catalogue — device types, hardware types, config
       * templates — and firmware/OTA. ANEP-only: every mutation is already
       * require_admin or require_superadmin on the API, so showing these to
       * a client's admin only offers them buttons that fail.
       */
      canAccessCatalog: isAnepStaff,

      /**
       * Firmware rollout across the fleet. ANEP-only for the same reason.
       */
      canAccessOtaUpdates: isAnepStaff,

      /**
       * Whether the user can open the Users section at all (read).
       * Admin is included: they may look at the directory.
       */
      canManageUsers,

      /**
       * Whether the user may create, edit, delete an account or change its
       * access. Mirrors require_user_write on the API.
       *
       * An ANEP Admin is excluded on purpose: they run the platform —
       * onboarding clients, editing templates, reading the audit log — but
       * account lifecycle belongs to SuperAdmin on the ANEP side and to a
       * client's own ClientAdmin inside their organisation. Showing them
       * buttons that answer 403 would be worse than hiding them.
       */
      canWriteUsers: isSuperAdmin || isClientAdminOrOperator,

      /**
       * Whether this account is client-side staff with no usable scope —
       * either never granted, revoked, or their whole client deactivated.
       * Such a session must not enter the app: "access disabled" means the
       * product is closed to them, not that their device list is empty.
       */
      /**
       * Whether this session must be shut out of the application entirely.
       *
       * Two independent reasons, and the first was missing:
       *
       * 1. No application role on THIS environment's Keycloak client. Roles
       *    live on `provisioning-{env}`, so holding Admin on dev says nothing
       *    about staging. Keycloak still signs such a visitor in — it does
       *    not require a role to issue a token — and the API then refuses
       *    every call. Letting them roam a shell where nothing loads is worse
       *    than telling them plainly.
       *
       *    Note this cannot be `user.roles.length`: the realm always adds
       *    `default-roles-…`, `offline_access` and `uma_authorization`, so
       *    that list is never empty and never meant anything here.
       *
       * 2. Client-side staff whose client scope is empty — never granted,
       *    revoked, or their whole client suspended. That one needs the
       *    server's answer, hence the `scopeResolved` guard; the role check
       *    above is known from the token alone and needs no round trip.
       */
      isLockedOut:
        !hasApplicationRole ||
        (scopeResolved &&
          !isAnepStaff && (isClientAdminOrOperator || isViewer) &&
          allowedClientIds.length === 0),

      /**
       * Whether the user can edit device types, hardware types, and model references
       */
      canEditDeviceTypes,

      /**
       * List of client IDs the user has access to
       * Empty for SuperAdmin (they have access to all)
       */
      allowedClientIds,

      /**
       * Whether the user has full (unrestricted) access to all data
       */
      hasFullAccess: isAnepStaff,

      /**
       * Whether user has only one client assigned
       */
      hasSingleClient,

      /**
       * Whether to show the Clients screen — ANEP-side tenant
       * administration: the list of every customer organisation, and the
       * flow that onboards new ones.
       *
       * Restricted to SuperAdmin / Admin. A ClientAdmin is the customer's
       * own administrator: showing them a directory of every other client
       * is both confusing and a disclosure they have no reason to see.
       * They manage their own organisation's users from the Users screen,
       * which scopes itself to the clients they belong to.
       *
       * Creating a client is separately gated by require_admin on the
       * backend, so this is about what is shown, not what is permitted.
       */
      shouldShowClientUI: isAnepStaff,

      /**
       * Check if user can access a specific client
       * @param clientId - The client ID to check
       * @returns true if user can access the client
       */
      canAccessClient: (clientId: string): boolean => {
        if (isSuperAdmin) return true;
        return allowedClientIds.includes(clientId);
      },

      /**
       * Filter an array to only include items the user can access
       * @param items - Array of items with client_id property
       * @returns Filtered array
       */
      filterByClientAccess: <T extends { client_id?: string | null }>(items: T[]): T[] => {
        if (isSuperAdmin) return items;
        return items.filter(item => {
          // Items without client_id (global) are accessible to everyone
          if (!item.client_id) return true;
          return allowedClientIds.includes(item.client_id);
        });
      },

      /**
       * Check if user has any client assignments
       * SuperAdmin always returns true even without explicit assignments
       */
      hasAnyClientAccess: isAnepStaff || allowedClientIds.length > 0,

      /**
       * Whether the user can view the audit log page.
       * Restricted to Admin and SuperAdmin (matches backend require_admin gate).
       */
      canViewAuditLog: isSuperAdmin || isAdmin,

      /**
       * Whether the user can create / edit devices. SuperAdmin always
       * allowed; Admin / ClientAdmin / Operator must additionally have
       * at least one client assignment, otherwise the backend's
       * allowed_clients filter would refuse the POST anyway (and the
       * UI affordance would mislead). Viewer-only never qualifies.
       */
      canManageDevices: isSuperAdmin || ((isAdmin || isClientAdminOrOperator) && allowedClientIds.length > 0)
    };
  }, [user?.roles, user?.clientIds, user?.isAnepStaff, serverScope]);
};

export default useClientAccess;
